import { Suspense, useState, type ReactNode } from "react";
import ContentLoadingScreen from "@/shared/components/Layout/ContentLoadingScreen";
import { lazyWithRetry } from "@/shared/utils/lazyWithRetry";
import { LOADING_LABEL, SUPPORT_TICKET_SUBJECT } from "../../configs/unpaidInvoices.config";
import { useUnpaidGate, type SupportDialog, type TransferReview } from "../../hooks/unpaid/useUnpaidGate";
import UnpaidBanner from "./UnpaidBanner";
import FakeConsoleSkeleton from "./backdrop/FakeConsoleSkeleton";
import type {
  ConsoleEntry, PaymentOutcome, PaymentSnapshot, SupportDialogProps, UnpaidBannerProps, UnpaidNoticeProps, UnpaidPayment,
} from "../../types/unpaidInvoices.types";

// Only rendered once an invoice is unpaid: keep them, and the payment stack they pull in, out of the entry chunk.
const UnpaidBlockedView = lazyWithRetry(() => import("./UnpaidBlockedView"));
const UnpaidPaymentDialog = lazyWithRetry(() => import("./UnpaidPaymentDialog"));
const UnpaidPaymentFlow = lazyWithRetry(() => import("./UnpaidPaymentFlow"));
const UnpaidTransferDialog = lazyWithRetry(() => import("./transfer/UnpaidTransferDialog"));
const ChangeAccountDialog = lazyWithRetry(() => import("../ChangeAccountDialog"));

type UnpaidInvoicesGateProps = {
  children: ReactNode
  renderSupport: (props: SupportDialogProps) => ReactNode
  onSignOut: () => void
};

type GateOverlaysProps = {
  payment: UnpaidPayment
  support: SupportDialog
  transferReview: TransferReview
  renderSupport: UnpaidInvoicesGateProps["renderSupport"]
  isChangingAccount: boolean
  onChangingAccount: (open: boolean) => void
};

type HeldPaymentFlowProps = {
  payment: UnpaidPayment
  snapshot: PaymentSnapshot
  consoleEntry: ConsoleEntry
  onOutcomeChange: (outcome: PaymentOutcome) => void
};

const HeldPaymentFlow = ({
  payment, snapshot, consoleEntry, onOutcomeChange,
}: HeldPaymentFlowProps) => (
  <Suspense fallback={null}>
    <UnpaidPaymentFlow
      rejectionIds={snapshot.rejectionIds}
      installmentIds={snapshot.installmentIds}
      totalCents={snapshot.totalCents}
      canDebit={payment.canDebit}
      publishableKey={payment.publishableKey}
      resumeClientSecret={snapshot.resumeClientSecret}
      context="blocked"
      host={{
        kind: "inline", onClose: payment.closePayment, onOutcomeChange, consoleEntry,
      }}
    />
  </Suspense>
);

const LoadingConsole = () => (
  <div role="status" aria-label={LOADING_LABEL}>
    <FakeConsoleSkeleton>
      <ContentLoadingScreen />
    </FakeConsoleSkeleton>
  </div>
);

// Keyed on where the flow opened, not on the access state: the state flips as soon as a payment is
// pending, and the dialog must survive that until the user closes it.
const GateOverlays = ({
  payment, support, transferReview, renderSupport, isChangingAccount, onChangingAccount,
}: GateOverlaysProps) => (
  <Suspense fallback={null}>
    {isChangingAccount && (
      <ChangeAccountDialog
        open
        onOpenChange={onChangingAccount}
        accounts={payment.accounts}
        codeChannels={payment.codeChannels}
      />
    )}
    {transferReview.isTransferReviewOpen && payment.openTransfer && (
      <UnpaidTransferDialog
        openTransfer={payment.openTransfer}
        onContactSupport={transferReview.contactSupportFromTransfer}
        onClose={transferReview.closeTransferReview}
      />
    )}
    {support.isSupportOpen && renderSupport({ open: true, onClose: support.closeSupport, defaultSubject: SUPPORT_TICKET_SUBJECT })}
    {payment.snapshot?.context === "warning" && (
      <UnpaidPaymentDialog
        rejectionIds={payment.snapshot.rejectionIds}
        installmentIds={payment.snapshot.installmentIds}
        totalCents={payment.snapshot.totalCents}
        canDebit={payment.canDebit}
        publishableKey={payment.publishableKey}
        resumeClientSecret={payment.snapshot.resumeClientSecret}
        context="warning"
        onClose={payment.closePayment}
      />
    )}
  </Suspense>
);

// A payment outcome (transfer instructions, debit confirmation) lives in the blocked view. The organization
// turns pending the moment it is created, which would unmount that view and the outcome with it.
const findHeldBlockedSnapshot = (payment: UnpaidPayment) => (
  payment.snapshot?.context === "blocked" ? payment.snapshot : undefined
);

type NoticeSources = {
  access: {
    amountCents: number
    isAdmin: boolean
    paymentMethodRequired: boolean
    staffMessage: string | null
  }
  payment: UnpaidPayment
  heldSnapshot: PaymentSnapshot | undefined
  onContactSupport: () => void
  onChangeAccount: () => void
};

const toNotice = ({
  access, payment, heldSnapshot, onContactSupport, onChangeAccount,
}: NoticeSources): UnpaidNoticeProps => ({
  amountCents: heldSnapshot ? heldSnapshot.noticeAmountCents : access.amountCents,
  pendingCents: payment.pendingCents,
  isAdmin: access.isAdmin,
  paymentMethodRequired: access.paymentMethodRequired,
  staffMessage: access.staffMessage,
  onChangeAccount: access.isAdmin && access.paymentMethodRequired ? onChangeAccount : undefined,
  payAction: payment.payAction,
  offerState: payment.offerState,
  onRetryOffer: payment.retryOffer,
  onContactSupport,
});

const toReviewTransfer = (
  pendingPayment: UnpaidBannerProps["pendingPayment"],
  payment: UnpaidPayment,
  transferReview: TransferReview,
) => (
  pendingPayment?.method === "bank_transfer" && payment.openTransfer ? transferReview.reviewTransfer : undefined
);

type BlockedGateProps = {
  payment: UnpaidPayment
  consoleEntry: ConsoleEntry
  heldSnapshot: PaymentSnapshot | undefined
  notice: UnpaidNoticeProps
  onSignOut: () => void
};

const BlockedGate = ({
  payment, consoleEntry, heldSnapshot, notice, onSignOut,
}: BlockedGateProps) => {
  const [outcome, setOutcome] = useState<PaymentOutcome>("none");
  const paymentFlow = heldSnapshot && (
    <HeldPaymentFlow
      payment={payment}
      snapshot={heldSnapshot}
      consoleEntry={consoleEntry}
      onOutcomeChange={setOutcome}
    />
  );

  return (
    <Suspense fallback={<LoadingConsole />}>
      <UnpaidBlockedView
        {...notice}
        paymentFlow={paymentFlow}
        isOutcomeShown={Boolean(heldSnapshot) && outcome !== "none"}
        onCloseOutcome={heldSnapshot && outcome === "closable" ? payment.closePayment : undefined}
        onSignOut={onSignOut}
      />
    </Suspense>
  );
};

/**
 * Wraps the router only. Unauthenticated, normal and warning states render the same fragment shape
 * so that `children` keeps its position and is never remounted when the banner comes and goes.
 */
const UnpaidInvoicesGate = ({ children, renderSupport, onSignOut }: UnpaidInvoicesGateProps) => {
  const {
    access, payment, consoleEntry, support, transferReview,
  } = useUnpaidGate();
  const { accessState } = access;
  const [isChangingAccount, setChangingAccount] = useState(false);

  if (accessState === "loading") return <LoadingConsole />;

  const heldSnapshot = findHeldBlockedSnapshot(payment);
  const notice = toNotice({
    access, payment, heldSnapshot, onContactSupport: support.openSupport, onChangeAccount: () => setChangingAccount(true),
  });
  const overlays = (
    <GateOverlays
      payment={payment}
      support={support}
      transferReview={transferReview}
      renderSupport={renderSupport}
      isChangingAccount={isChangingAccount}
      onChangingAccount={(open) => {
        setChangingAccount(open);
        // Accepted, the new RIB lets the pause go in the CRM: closing the dialog shows the console again.
        if (!open) payment.refreshAccess();
      }}
    />
  );

  if (accessState === "blocked" || heldSnapshot) {
    return (
      <>
        <BlockedGate
          payment={payment}
          consoleEntry={consoleEntry}
          heldSnapshot={heldSnapshot}
          notice={notice}
          onSignOut={onSignOut}
        />
        {overlays}
      </>
    );
  }

  return (
    <>
      {accessState === "warning" && (
        <UnpaidBanner
          {...notice}
          pendingPayment={access.pendingPayment}
          onReviewTransfer={toReviewTransfer(access.pendingPayment, payment, transferReview)}
        />
      )}
      {children}
      {overlays}
    </>
  );
};

export default UnpaidInvoicesGate;
